/**
 * The UI's surface lists, derived from the surface manifests.
 *
 * The manifests live in the Agent Atlas service
 * (saf3ai-agent-atlas-microservice/src/saf3ai_agent_atlas/surfaces/manifests)
 * and are served at GET /agent-atlas/api/v1/surfaces. These functions turn that
 * response into the shapes the Atlas pages have always used, so a new surface
 * is one manifest file and never an edit here.
 *
 * Pure and synchronous on purpose: everything about fetching lives in
 * useSurfaces.js, and these are what the parity test pins.
 */

const ALL = Object.freeze({ id: 'all', label: 'All' });

/** Surfaces listed in one switcher, in the position each manifest gives. */
const inSwitcher = (surfaces, name) => surfaces
  .filter((s) => s.switchers && s.switchers[name] != null)
  .sort((a, b) => a.switchers[name] - b.switchers[name]);

/** True when any of the surface's sensors declares captured prompt text. */
export const capturesPrompts = (s) => Object.values(s.sensors || {})
  .some((sensor) => (sensor.caps || []).includes('content.prompt'));

const pill = (labelOf) => (s) => ({ id: s.id, label: labelOf(s) });

export const deriveSurfaceLists = (surfaces = []) => {
  const byLabel = pill((s) => s.label);

  // Compliance surface switcher (and the Atlas audit report).
  const SURFACE_KEYS = [ALL, ...inSwitcher(surfaces, 'compliance').map(byLabel)];

  // Alerts cover modules outside the compliance universe too.
  const ALERT_SURFACE_KEYS = [ALL, ...inSwitcher(surfaces, 'alerts').map(byLabel)];

  // FinOps lists the surfaces that carry token telemetry. A manifest can
  // override the label for this switcher only.
  const FINOPS_SURFACE_KEYS = [
    ALL,
    ...inSwitcher(surfaces, 'finops').map(pill((s) => s.finops?.label || s.label)),
  ];

  // Adoption (Conversations / Knowledge gaps / Intent analysis): the surfaces
  // whose interactions the ingest analyzer classifies. The analyzer reads the
  // prompt, so a surface none of whose sensors capture prompt text (Gemini
  // Workspace's audit feed today) has no conversations, gaps or intents to
  // show and is not offered as a filter.
  const ADOPTION_SURFACE_KEYS = [
    ALL,
    ...inSwitcher(surfaces, 'adoption').filter(capturesPrompts).map(byLabel),
  ];

  // Deep links into each surface's own module. `module_label` names the
  // module as a destination ("GCP policy"); it falls back to the label.
  const MODULE_ROUTES = Object.fromEntries(surfaces.map((s) => [
    s.id,
    { label: s.module_label || s.label, ...(s.routes || {}) },
  ]));

  // Surface id → the raw `module` values its alert documents carry.
  const SURFACE_TO_ALERT_MODULES = Object.fromEntries(surfaces
    .filter((s) => (s.ids?.alert_modules || []).length > 0)
    .map((s) => [s.id, s.ids.alert_modules]));

  return {
    SURFACE_KEYS,
    ALERT_SURFACE_KEYS,
    FINOPS_SURFACE_KEYS,
    ADOPTION_SURFACE_KEYS,
    MODULE_ROUTES,
    SURFACE_TO_ALERT_MODULES,
  };
};
